import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons-react";
import "@mantine/dates/styles.css";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  DeputiesResponse,
  DeputyUser,
  DeputyRoleGroup,
  SaveDeputiesRequest,
  getDeputies,
  saveDeputies,
} from "../../../services/DeputiesService";
import UserContractSelect from "../UserContractSelect";

interface DeputiesPageProps {
  onBack?: () => void;
  initialData?: DeputiesResponse | null;
  onDataLoaded?: (data: DeputiesResponse) => void;
}

interface DeputyRowState {
  awayStart: Date | null;
  awayEnd: Date | null;
  substituteUserId: number | null;
}

const DATE_FORMAT = "DD.MM.YYYY";
const emptyData: DeputiesResponse = {
  availableContracts: [],
  selectedContractId: null,
  archiveEmail: "",
  contractorArchiveEmail: "",
  roleGroups: [],
};

const roleGridColumns =
  "minmax(180px, 1.2fr) minmax(130px, 0.8fr) minmax(130px, 0.8fr) minmax(220px, 1.5fr)";

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const toIsoDate = (value: Date | null): string | null => {
  if (!value) return null;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isForbiddenError = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 403;

const hasCompleteRange = (row?: DeputyRowState | null) =>
  Boolean(row?.awayStart && row.awayEnd);

const rangesOverlap = (
  firstStart: Date,
  firstEnd: Date,
  secondStart: Date,
  secondEnd: Date,
) => firstStart <= secondEnd && secondStart <= firstEnd;

const rowOverlaps = (
  first?: DeputyRowState | null,
  second?: DeputyRowState | null,
) =>
  Boolean(
    first?.awayStart &&
    first.awayEnd &&
    second?.awayStart &&
    second.awayEnd &&
    rangesOverlap(
      first.awayStart,
      first.awayEnd,
      second.awayStart,
      second.awayEnd,
    )
  );

const isCandidateUnavailable = (
  candidateId: number,
  currentUserId: number,
  currentRow: DeputyRowState,
  rows: Record<number, DeputyRowState>,
) => {
  if (!hasCompleteRange(currentRow)) return false;

  const candidateRow = rows[candidateId];
  if (rowOverlaps(currentRow, candidateRow)) {
    return true;
  }

  return Object.entries(rows).some(([userId, row]) => {
    const assignedUserId = Number(userId);
    return (
      assignedUserId !== currentUserId &&
      row.substituteUserId === candidateId &&
      rowOverlaps(currentRow, row)
    );
  });
};

const createsCircularDependency = (
  currentUserId: number,
  candidateId: number,
  currentRow: DeputyRowState,
  rows: Record<number, DeputyRowState>,
) => {
  if (!hasCompleteRange(currentRow)) return false;

  const visited = new Set<number>();
  let nextUserId: number | null = candidateId;

  while (nextUserId) {
    if (nextUserId === currentUserId) return true;
    if (visited.has(nextUserId)) return false;
    visited.add(nextUserId);

    const nextRow: DeputyRowState | undefined = rows[nextUserId];
    if (!rowOverlaps(currentRow, nextRow)) return false;
    nextUserId = nextRow?.substituteUserId ?? null;
  }

  return false;
};

const getInvalidSubstituteReason = (
  currentUserId: number,
  substituteUserId: number,
  currentRow: DeputyRowState,
  rows: Record<number, DeputyRowState>,
) => {
  if (substituteUserId === currentUserId) return "same-person";
  if (isCandidateUnavailable(substituteUserId, currentUserId, currentRow, rows)) {
    return "unavailable";
  }
  if (
    createsCircularDependency(
      currentUserId,
      substituteUserId,
      currentRow,
      rows,
    )
  ) {
    return "circular";
  }
  return null;
};

const getValidSubstituteOptions = (
  user: DeputyUser,
  row: DeputyRowState,
  rows: Record<number, DeputyRowState>,
) =>
  user.substituteOptions.filter(
    (option) =>
      getInvalidSubstituteReason(user.userId, option.id, row, rows) == null,
  );

export default function DeputiesPage({
  onBack: _onBack,
  initialData,
  onDataLoaded,
}: Readonly<DeputiesPageProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 521,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();
  const canManage = checkAccess("CITY_OBJECT_DEPUTY_MANAGEMENT");

  const [data, setData] = useState<DeputiesResponse>(initialData ?? emptyData);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    initialData?.selectedContractId ?? null,
  );
  const [archiveEmail, setArchiveEmail] = useState(
    initialData?.archiveEmail ?? "",
  );
  const [contractorArchiveEmail, setContractorArchiveEmail] = useState(
    initialData?.contractorArchiveEmail ?? "",
  );
  const [rows, setRows] = useState<Record<number, DeputyRowState>>({});
  const [updateAllContracts, setUpdateAllContracts] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const loadSeqRef = useRef(0);
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const t = useCallback(
    (key: string, fallback: string) => getTranslation(key) || fallback,
    [getTranslation],
  );

  const hydrateFromResponse = useCallback((response: DeputiesResponse) => {
    const nextRows: Record<number, DeputyRowState> = {};
    response.roleGroups.forEach((group) => {
      group.users.forEach((user) => {
        nextRows[user.userId] = {
          awayStart: parseDate(user.awayStart),
          awayEnd: parseDate(user.awayEnd),
          substituteUserId: user.substituteUserId ?? null,
        };
      });
    });

    setData(response);
    setSelectedContractId(response.selectedContractId ?? null);
    setArchiveEmail(response.archiveEmail ?? "");
    setContractorArchiveEmail(response.contractorArchiveEmail ?? "");
    setRows(nextRows);
  }, []);

  const showError = useCallback(
    (fallback: string, error: unknown) => {
      console.error(error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error instanceof Error ? error.message : fallback);
      notifications.show({
        title: t("REPORTS_ERROR", "Error"),
        message,
        color: "red",
      });
    },
    [t],
  );

  const publishData = useCallback(
    (response: DeputiesResponse) => {
      onDataLoaded?.(response);
    },
    [onDataLoaded],
  );

  useEffect(() => {
    if (initialData) {
      hydrateFromResponse(initialData);
    }
  }, [hydrateFromResponse, initialData]);

  useEffect(() => {
    if (!canManage) {
      setAccessDenied(true);
      return;
    }
    setAccessDenied(false);
  }, [canManage]);

  const loadDeputiesForContract = useCallback(
    async (contractId: number) => {
      const seq = ++loadSeqRef.current;
      setLoading(true);
      try {
        const result = await getDeputies(selectedAddressSystemId, contractId);
        if (seq !== loadSeqRef.current) return;

        hydrateFromResponse(result);
        publishData(result);
      } catch (error) {
        if (seq !== loadSeqRef.current) return;

        if (isForbiddenError(error)) {
          setAccessDenied(true);
        } else {
          showError("Failed to load deputies for contract", error);
        }
      } finally {
        if (seq === loadSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [hydrateFromResponse, publishData, selectedAddressSystemId, showError],
  );

  const handleContractChange = (contractId: number | null) => {
    loadSeqRef.current += 1;
    setSelectedContractId(contractId);
    if (contractId) {
      void loadDeputiesForContract(contractId);
      return;
    }

    setLoading(false);
    hydrateFromResponse({
      ...emptyData,
      availableContracts: data.availableContracts,
    });
  };

  const updateRow = (
    userId: number,
    patch: Partial<DeputyRowState>,
  ) => {
    setRows((prev) => {
      const nextRows = {
        ...prev,
        [userId]: {
          awayStart: prev[userId]?.awayStart ?? null,
          awayEnd: prev[userId]?.awayEnd ?? null,
          substituteUserId: prev[userId]?.substituteUserId ?? null,
          ...patch,
        },
      };

      data.roleGroups.forEach((group) => {
        group.users.forEach((user) => {
          const row = nextRows[user.userId];
          if (!row?.substituteUserId) return;

          const reason = getInvalidSubstituteReason(
            user.userId,
            row.substituteUserId,
            row,
            nextRows,
          );
          if (reason) {
            nextRows[user.userId] = {
              ...row,
              substituteUserId: null,
            };
          }
        });
      });

      return nextRows;
    });
  };

  const allUsers = useMemo(
    () =>
      data.roleGroups.flatMap((group) =>
        group.users.map((user) => ({
          user,
          row: rows[user.userId],
        })),
      ),
    [data.roleGroups, rows],
  );

  const validate = () => {
    for (const { user, row } of allUsers) {
      const hasStart = Boolean(row?.awayStart);
      const hasEnd = Boolean(row?.awayEnd);
      const hasSubstitute = Boolean(row?.substituteUserId);

      if (hasStart || hasEnd || hasSubstitute) {
        if (!hasStart || !hasEnd || !hasSubstitute) {
          notifications.show({
            title: t("REPORTS_ERROR", "Error"),
            message: t(
              "DEPUTIES_ROW_REQUIRED",
              `Away start, away end, and substitute are required for ${user.name}.`,
            ),
            color: "red",
          });
          return false;
        }
        if (row?.substituteUserId === user.userId) {
          notifications.show({
            title: t("REPORTS_ERROR", "Error"),
            message: t(
              "TEXT_ALERT_SAMA_HENKILO",
              "Substitute cannot be the same person.",
            ),
            color: "red",
          });
          return false;
        }
        if (row?.substituteUserId) {
          const reason = getInvalidSubstituteReason(
            user.userId,
            row.substituteUserId,
            row,
            rows,
          );
          if (reason === "unavailable") {
            notifications.show({
              title: t("REPORTS_ERROR", "Error"),
              message: t(
                "DEPUTIES_SUBSTITUTE_UNAVAILABLE",
                `Selected substitute is not available for ${user.name}.`,
              ),
              color: "red",
            });
            return false;
          }
          if (reason === "circular") {
            notifications.show({
              title: t("REPORTS_ERROR", "Error"),
              message: t(
                "DEPUTIES_SUBSTITUTE_CIRCULAR",
                `Selected substitute would create a circular deputy assignment for ${user.name}.`,
              ),
              color: "red",
            });
            return false;
          }
        }
        if (row?.awayStart && row.awayEnd && row.awayStart > row.awayEnd) {
          notifications.show({
            title: t("REPORTS_ERROR", "Error"),
            message: t(
              "DEPUTIES_DATE_ORDER",
              `Away start must be before away end for ${user.name}.`,
            ),
            color: "red",
          });
          return false;
        }
      }
    }

    return true;
  };

  const buildRequest = (): SaveDeputiesRequest => {
    const uniqueUsers = new Map<
      number,
      SaveDeputiesRequest["deputies"][number]
    >();
    data.roleGroups.forEach((group) => {
      group.users.forEach((user) => {
        const row = rows[user.userId] ?? {
          awayStart: null,
          awayEnd: null,
          substituteUserId: null,
        };
        uniqueUsers.set(user.userId, {
          userId: user.userId,
          awayStart: toIsoDate(row.awayStart),
          awayEnd: toIsoDate(row.awayEnd),
          substituteUserId: row.substituteUserId,
        });
      });
    });

    return {
      contractId: selectedContractId!,
      updateAllContracts,
      archiveEmail: archiveEmail.trim() || null,
      contractorArchiveEmail: contractorArchiveEmail.trim() || null,
      deputies: Array.from(uniqueUsers.values()),
    };
  };

  const handleSave = async () => {
    if (!selectedContractId || !validate()) return;

    setSaving(true);
    try {
      const result = await saveDeputies(
        selectedAddressSystemId,
        buildRequest(),
      );
      hydrateFromResponse(result);
      publishData(result);
      notifications.show({
        title: t("CREWS_SUCCESS", "Success"),
        message: t("DEPUTIES_SAVED", "Deputies saved."),
        color: "green",
      });
    } catch (error) {
      showError("Failed to save deputies", error);
    } finally {
      setSaving(false);
    }
  };

  const renderGroup = (group: DeputyRoleGroup) => (
    <Paper key={group.role} withBorder radius="md">
      <Group
        justify="space-between"
        px="md"
        py="sm"
        bg="var(--mantine-color-gray-1)"
      >
        <Text fw={700}>{t(group.labelTranslationKey, group.defaultLabel)}</Text>
        <Text size="xs" c="dimmed">
          {t("DEPUTIES_USER_COUNT", `${group.users.length} users`).replace(
            "{count}",
            String(group.users.length),
          )}
        </Text>
      </Group>
      {group.users.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="lg">
          {t("DEPUTIES_NO_USERS", "No users found for this role.")}
        </Text>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          <Box
            bg="var(--mantine-color-gray-3)"
            style={{
              display: "grid",
              gridTemplateColumns: roleGridColumns,
              minWidth: 720,
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            {[
              t("TEXT_EMAIL_PERSON", "Person"),
              t("TEXT_EMAIL_START", "Start"),
              t("TEXT_EMAIL_END", "End"),
              t("TEXT_EMAIL_SUBSTITUTE", "Substitute"),
            ].map((label) => (
              <Text
                key={label}
                size="xs"
                fw={700}
                px="sm"
                py="xs"
                c="dark.8"
                tt="uppercase"
                style={{ letterSpacing: "0.03em" }}
              >
                {label}
              </Text>
            ))}
          </Box>
          <Stack gap={0} style={{ minWidth: 720 }}>
            {group.users.map((user, index) => {
              const row = rows[user.userId] ?? {
                awayStart: null,
                awayEnd: null,
                substituteUserId: null,
              };
              const substituteOptions = getValidSubstituteOptions(
                user,
                row,
                rows,
              );
              return (
                <Box
                  key={`${group.role}-${user.userId}`}
                  bg={index % 2 === 0 ? "white" : "var(--mantine-color-gray-0)"}
                  style={{
                    display: "grid",
                    gridTemplateColumns: roleGridColumns,
                    alignItems: "center",
                    minHeight: 52,
                    borderTop: "1px solid var(--mantine-color-gray-2)",
                  }}
                >
                  <Box px="sm" py={6}>
                    <Text size="sm" fw={500}>
                      {user.name}
                    </Text>
                  </Box>
                  <Box px="sm" py={6}>
                    <DateInput
                      value={row.awayStart}
                      onChange={(value) => {
                        const awayEnd =
                          value && row.awayEnd && row.awayEnd < value
                            ? null
                            : row.awayEnd;
                        updateRow(user.userId, { awayStart: value, awayEnd });
                      }}
                      valueFormat={DATE_FORMAT}
                      minDate={today}
                      clearable
                      size="xs"
                    />
                  </Box>
                  <Box px="sm" py={6}>
                    <DateInput
                      value={row.awayEnd}
                      onChange={(value) =>
                        updateRow(user.userId, { awayEnd: value })
                      }
                      valueFormat={DATE_FORMAT}
                      minDate={row.awayStart ?? today}
                      clearable
                      size="xs"
                    />
                  </Box>
                  <Box px="sm" py={6}>
                    <Select
                      data={substituteOptions.map((option) => ({
                        value: String(option.id),
                        label: option.name,
                      }))}
                      value={
                        row.substituteUserId
                          ? String(row.substituteUserId)
                          : null
                      }
                      onChange={(value) =>
                        updateRow(user.userId, {
                          substituteUserId: value ? Number(value) : null,
                        })
                      }
                      placeholder={t(
                        "DEPUTIES_SELECT_SUBSTITUTE",
                        "Select substitute",
                      )}
                      searchable
                      clearable
                      size="xs"
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Paper>
  );

  return (
    <Card h="100%" withBorder radius="lg" px="xl" py="lg" shadow="sm">
      <Stack h="100%" gap="md">
        <Group justify="space-between">
          <Text fw={700}>{t("TEXT_LINK_DEPUTY", "Deputies")}</Text>
          {selectedContractId && !accessDenied ? (
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              loading={saving}
              onClick={() => {
                void handleSave();
              }}
            >
              {t("COMMON_SAVE", "Save")}
            </Button>
          ) : null}
        </Group>

        {accessDenied ? (
          <Text c="dimmed" size="sm">
            {t(
              "SETTINGS_NO_LINKS_FOR_ROLE",
              "No settings links match your permissions.",
            )}
          </Text>
        ) : (
          <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
            <UserContractSelect
              contracts={data.availableContracts}
              label={t("TEXT_LABEL_VALITSE_URAKKA", "Contract")}
              placeholder={t(
                "DEPUTIES_SELECT_CONTRACT_PLACEHOLDER",
                "Select a contract",
              )}
              emptyHint={t(
                "DEPUTIES_NO_CONTRACTS",
                "No contracts available for this address system.",
              )}
              value={selectedContractId}
              onChange={handleContractChange}
            />

            {!selectedContractId ? (
              <Text c="dimmed" size="sm">
                {t(
                  "DEPUTIES_SELECT_CONTRACT_FIRST",
                  "Select a contract to manage deputies.",
                )}
              </Text>
            ) : loading ? (
              <Group justify="center" style={{ flex: 1 }}>
                <Loader size="sm" />
              </Group>
            ) : (
              <Stack
                gap="md"
                style={{ flex: 1, minHeight: 0, overflow: "auto" }}
              >
                {data.roleGroups.map(renderGroup)}

                <Paper withBorder radius="md" p="md">
                  <Stack gap="sm">
                    <Text fw={700}>
                      {t("TEXT_LABEL_COMMON_ADDRESS", "Common address")}
                    </Text>
                    <TextInput
                      label={t("TEXT_LABEL_ARCHIVE_EMAIL", "Archive email")}
                      value={archiveEmail}
                      onChange={(event) =>
                        setArchiveEmail(event.currentTarget.value)
                      }
                    />
                    <TextInput
                      label={t(
                        "TEXT_LABEL_CONT_ARCHIVE_EMAIL",
                        "Contractor archive email",
                      )}
                      value={contractorArchiveEmail}
                      onChange={(event) =>
                        setContractorArchiveEmail(event.currentTarget.value)
                      }
                    />
                  </Stack>
                </Paper>

                <Radio.Group
                  value={updateAllContracts ? "all" : "selected"}
                  onChange={(value) =>
                    setUpdateAllContracts(value !== "selected")
                  }
                >
                  <Stack gap="xs">
                    <Radio
                      value="all"
                      label={t(
                        "DEPUTIES_UPDATE_ALL_CONTRACTS",
                        "Update deputies for all contracts",
                      )}
                    />
                    <Radio
                      value="selected"
                      label={t(
                        "DEPUTIES_UPDATE_SELECTED_CONTRACT",
                        "Update deputies only for this contract",
                      )}
                    />
                  </Stack>
                </Radio.Group>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
